'use client';

import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type { SetupNextStepResponse } from '@borradh-workspace/api-client/types';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

export const setupNextStepQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.setupProgress.nextStep(),
    queryFn: ({ signal }) =>
      apiClient.get<SetupNextStepResponse>('setup-progress/next-step', {
        signal,
      }),
    // Short: the owner fixes a step elsewhere (Meta, Settings) and comes
    // back; a refetch on return should show the next one.
    staleTime: 30_000,
  });

/** The next ads setup step for the active clinic; null when there is none. */
export const useSetupNextStep = () => {
  const query = useQuery(setupNextStepQueryOptions());
  return {
    nextStep: query.data?.nextStep ?? null,
    isLoading: query.isLoading,
  };
};

/**
 * "I've done this": the API checks the clinic's setup again now (asking
 * Meta afresh) and returns the step that is next after that.
 */
export const useCheckSetupAgain = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      apiClient.post<SetupNextStepResponse>(
        'setup-progress/next-step/check',
        {}
      ),
    onSuccess: (result, _vars, _ctx) => {
      const before = queryClient.getQueryData<SetupNextStepResponse>(
        queryKeys.setupProgress.nextStep()
      );
      queryClient.setQueryData(queryKeys.setupProgress.nextStep(), result);
      if (
        before?.nextStep?.step &&
        before.nextStep.step === result.nextStep?.step
      )
        toast.message('Not done yet', {
          description:
            'Meta still shows this step as open. It can take a few minutes after you finish.',
        });
      else toast.success('Done. Thanks!');
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Could not check again'),
  });
  return { checkAgain: mutation.mutate, isChecking: mutation.isPending };
};
